import React, { useCallback, useEffect, useRef, useState } from 'react';
import './imageLightbox.css';

export interface ImageLightboxToolbarAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  title?: string;
  onClick: () => void;
}

export interface ImageLightboxProps {
  imageSrc: string | null;
  onClose: () => void;
  index?: number;
  total?: number;
  onPrev?: () => void;
  onNext?: () => void;
  canPrev?: boolean;
  canNext?: boolean;
  toolbarActions?: ImageLightboxToolbarAction[];
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 1.25;
const FIT_EPS = 0.02;

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

const ZoomablePreviewImage: React.FC<{ src: string }> = ({ src }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const fitScaleRef = useRef(1);
  const naturalRef = useRef({ w: 0, h: 0 });
  const dragRef = useRef<{
    x: number;
    y: number;
    ox: number;
    oy: number;
    moved: boolean;
  } | null>(null);
  /** pointerup 会先于 click，需在松手后吃掉「误触」的 click，避免拖完把缩放重置为适应 */
  const suppressNextClickRef = useRef(false);

  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [ready, setReady] = useState(false);

  const recomputeFit = useCallback(() => {
    const vp = viewportRef.current;
    const img = imgRef.current;
    if (!vp || !img || !naturalRef.current.w) return;
    const vpW = vp.clientWidth;
    const vpH = vp.clientHeight;
    if (vpW <= 0 || vpH <= 0) return;
    const s = Math.min(vpW / naturalRef.current.w, vpH / naturalRef.current.h, 1);
    fitScaleRef.current = s > 0 ? s : 1;
    setReady(true);
  }, []);

  useEffect(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setReady(false);
    naturalRef.current = { w: 0, h: 0 };
    suppressNextClickRef.current = false;
  }, [src]);

  const displayScale = fitScaleRef.current * zoom;
  const displayW = naturalRef.current.w * displayScale;
  const displayH = naturalRef.current.h * displayScale;

  const getViewportSize = () => {
    const vp = viewportRef.current;
    return { w: vp?.clientWidth ?? 0, h: vp?.clientHeight ?? 0 };
  };

  const canPan = useCallback(() => {
    if (!ready || zoom <= MIN_ZOOM + FIT_EPS) return false;
    const { w: vpW, h: vpH } = getViewportSize();
    return displayW > vpW + 1 || displayH > vpH + 1;
  }, [ready, zoom, displayW, displayH]);

  const clampOffset = useCallback(
    (ox: number, oy: number, z: number) => {
      const { w: vpW, h: vpH } = getViewportSize();
      const scale = fitScaleRef.current * z;
      const dw = naturalRef.current.w * scale;
      const dh = naturalRef.current.h * scale;
      if (dw <= vpW + 1 && dh <= vpH + 1) return { x: 0, y: 0 };
      const maxX = Math.max(0, (dw - vpW) / 2);
      const maxY = Math.max(0, (dh - vpH) / 2);
      return { x: clamp(ox, -maxX, maxX), y: clamp(oy, -maxY, maxY) };
    },
    [],
  );

  const applyZoom = useCallback(
    (nextZoom: number | ((prev: number) => number), anchor?: { x: number; y: number }) => {
      setZoom((prevZ) => {
        const raw = typeof nextZoom === 'function' ? nextZoom(prevZ) : nextZoom;
        const z = clamp(raw, MIN_ZOOM, MAX_ZOOM);
        setOffset((prev) => {
          if (z <= MIN_ZOOM + FIT_EPS) return { x: 0, y: 0 };
          if (!anchor || !viewportRef.current) return clampOffset(prev.x, prev.y, z);
          const vp = viewportRef.current.getBoundingClientRect();
          const cx = anchor.x - vp.left - vp.width / 2;
          const cy = anchor.y - vp.top - vp.height / 2;
          const ratio = z / prevZ;
          return clampOffset(
            prev.x * ratio + cx * (1 - ratio),
            prev.y * ratio + cy * (1 - ratio),
            z,
          );
        });
        return z;
      });
    },
    [clampOffset],
  );

  const onImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    naturalRef.current = { w: img.naturalWidth, h: img.naturalHeight };
    recomputeFit();
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const ro = new ResizeObserver(() => recomputeFit());
    ro.observe(vp);
    return () => ro.disconnect();
  }, [recomputeFit]);

  useEffect(() => {
    if (zoom <= MIN_ZOOM + FIT_EPS) {
      setOffset({ x: 0, y: 0 });
    }
  }, [zoom]);

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const factor = e.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP;
    applyZoom((z) => z * factor, { x: e.clientX, y: e.clientY });
  };

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    if (dragRef.current?.moved) return;
    if (zoom <= MIN_ZOOM + FIT_EPS) {
      applyZoom(Math.min(MAX_ZOOM, ZOOM_STEP * 1.5), { x: e.clientX, y: e.clientY });
    } else {
      applyZoom(MIN_ZOOM);
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!canPan() || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y, moved: false };
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    e.preventDefault();
    if (Math.abs(e.clientX - d.x) > 4 || Math.abs(e.clientY - d.y) > 4) {
      d.moved = true;
    }
    const nx = d.ox + (e.clientX - d.x);
    const ny = d.oy + (e.clientY - d.y);
    setOffset(clampOffset(nx, ny, zoom));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    if (dragRef.current.moved) {
      suppressNextClickRef.current = true;
    }
    dragRef.current = null;
    setDragging(false);
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const pannable = canPan();

  return (
    <div className="ig-lightbox-zoom-wrap">
      <div className="ig-lightbox-zoom-bar" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="ig-lightbox-zoom-btn"
          title="缩小"
          aria-label="缩小"
          onClick={() => applyZoom((z) => z / ZOOM_STEP)}
        >
          −
        </button>
        <span className="ig-lightbox-zoom-label">{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          className="ig-lightbox-zoom-btn"
          title="放大"
          aria-label="放大"
          onClick={() => applyZoom((z) => z * ZOOM_STEP)}
        >
          +
        </button>
        <button
          type="button"
          className="ig-lightbox-zoom-btn ig-lightbox-zoom-btn--fit"
          title="适应屏幕"
          aria-label="适应屏幕"
          onClick={() => applyZoom(MIN_ZOOM)}
        >
          适应
        </button>
      </div>
      <div
        ref={viewportRef}
        className={`ig-lightbox-viewport${pannable ? ' ig-lightbox-viewport--pan' : ''}${
          dragging ? ' ig-lightbox-viewport--dragging' : ''
        }`}
        onWheel={onWheel}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="ig-lightbox-img-stage"
          style={{
            width: naturalRef.current.w || undefined,
            height: naturalRef.current.h || undefined,
            transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${displayScale})`,
            cursor: pannable ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
          }}
          onClick={onClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <img
            ref={imgRef}
            src={src}
            alt=""
            referrerPolicy="no-referrer"
            draggable={false}
            onLoad={onImgLoad}
          />
        </div>
      </div>
    </div>
  );
};

/** 与生成图像页一致的大图预览灯箱（生成图像、素材库图片等共用）。 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  imageSrc,
  onClose,
  index,
  total = 0,
  onPrev,
  onNext,
  canPrev = false,
  canNext = false,
  toolbarActions,
}) => {
  const showNav = total > 1 && onPrev && onNext;
  const showCount = total > 0 && index != null;
  const showToolbar = (toolbarActions && toolbarActions.length > 0) || showCount;

  useEffect(() => {
    if (!imageSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (!showNav) return;
      if (e.key === 'ArrowLeft' && canPrev) onPrev?.();
      if (e.key === 'ArrowRight' && canNext) onNext?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [imageSrc, onClose, showNav, canPrev, canNext, onPrev, onNext]);

  if (!imageSrc) return null;

  return (
    <div
      className="ig-lightbox-mask"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button type="button" className="ig-lightbox-close" onClick={onClose} aria-label="关闭预览">
        ✕
      </button>
      {showNav ? (
        <>
          <button
            type="button"
            className="ig-lightbox-nav ig-lightbox-nav--prev"
            disabled={!canPrev}
            onClick={() => canPrev && onPrev?.()}
            aria-label="上一张"
          >
            ‹
          </button>
          <button
            type="button"
            className="ig-lightbox-nav ig-lightbox-nav--next"
            disabled={!canNext}
            onClick={() => canNext && onNext?.()}
            aria-label="下一张"
          >
            ›
          </button>
        </>
      ) : null}
      <div className="ig-lightbox-frame" role="dialog" aria-modal="true" aria-label="大图预览">
        <div
          className={`ig-lightbox-frame-stack${showToolbar ? '' : ' ig-lightbox-frame-stack--solo'}`}
        >
          <ZoomablePreviewImage key={imageSrc} src={imageSrc} />
          {showToolbar ? (
            <div
              className="ig-lightbox-toolbar"
              role="toolbar"
              aria-label="图片操作"
              onClick={(e) => e.stopPropagation()}
            >
              {toolbarActions?.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="ig-lightbox-tool"
                  title={action.title}
                  onClick={action.onClick}
                >
                  {action.icon ? <span aria-hidden>{action.icon}</span> : null}
                  {action.label}
                </button>
              ))}
              {showCount ? (
                <div className="ig-lightbox-count" aria-live="polite">
                  {(index ?? 0) + 1} / {total}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ImageLightbox;
